import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const NICHES = ["E-commerce", "Gaming", "Local Services", "Real Estate", "Digital Products"];
const TONES = ["Confident & direct", "Playful & witty", "Warm & helpful", "Technical & precise", "Bold & edgy"];

export default function CreateWorkspaceDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    name: "", niche: "E-commerce", brand_name: "",
    target_audience: "", tone: "Confident & direct", core_media_assets: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.brand_name) {
      toast.error("Workspace name and brand name are required.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/workspaces", form);
      toast.success("Workspace created.");
      onOpenChange(false);
      setForm({ name: "", niche: "E-commerce", brand_name: "", target_audience: "", tone: "Confident & direct", core_media_assets: "" });
      onCreated?.(res.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create workspace.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="create-workspace-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">New workspace</DialogTitle>
          <DialogDescription>One brand or game per workspace. You can edit this later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Workspace name</Label>
              <Input data-testid="ws-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Acme Store" />
            </div>
            <div className="space-y-2">
              <Label>Brand / Game name</Label>
              <Input data-testid="ws-brand-input" value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} placeholder="Acme" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Niche</Label>
              <Select value={form.niche} onValueChange={(v) => set("niche", v)}>
                <SelectTrigger data-testid="ws-niche-select"><SelectValue /></SelectTrigger>
                <SelectContent>{NICHES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tone of voice</Label>
              <Select value={form.tone} onValueChange={(v) => set("tone", v)}>
                <SelectTrigger data-testid="ws-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Target audience</Label>
            <Input data-testid="ws-audience-input" value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)} placeholder="Indie devs shipping side projects" />
          </div>
          <div className="space-y-2">
            <Label>Core media assets / key facts</Label>
            <Textarea data-testid="ws-assets-input" value={form.core_media_assets} onChange={(e) => set("core_media_assets", e.target.value)}
              placeholder="Specs, stats, features, mechanics, pricing… the concrete data the AI must reference." rows={4} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="ws-submit-button" onClick={submit} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create workspace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
